import { applyCouponDiscount } from '@/entities/reservation'
import { BUSINESS_HOUR_LABELS } from '@/features/seoPlaceDetail/lib/businessHours'
import {
  type IProductGroup,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'

import { parsePrice } from './parsePrice'
import {
  CAR_WASH_SECTION,
  ENGINE_OIL_SECTION,
  findCarWashCheapestProduct,
  findCheapestProduct,
  getSubjectParticle,
  type IProductForPrice,
  SECTION_NAME_BY_CODE,
  SECTION_ORDER_INDEX,
  TIRE_SECTION,
} from './placeHelpers'

export interface IFaqEntry {
  question: string
  answer: string
}

const MAX_EXTRA_CATEGORIES = 2

const sortSections = (sections: string[]): string[] =>
  sections
    .filter(s => SECTION_ORDER_INDEX[s] !== undefined)
    .sort(
      (a, b) =>
        (SECTION_ORDER_INDEX[a] ?? Number.POSITIVE_INFINITY) -
        (SECTION_ORDER_INDEX[b] ?? Number.POSITIVE_INFINITY)
    )

const findSectionPrice = (
  productGroups: IProductGroup[],
  section: string
): number => {
  const group = productGroups.find(g => g.section === section)
  const raw = parsePrice(group?.lowestPriceTargetProduct?.salePrice)
  if (raw <= 0) return 0
  return applyCouponDiscount(section, raw)
}

const pickExtraCategoryNames = (
  primarySection: string,
  sortedSections: string[]
): string[] =>
  sortedSections
    .filter(s => s !== primarySection)
    .slice(0, MAX_EXTRA_CATEGORIES)
    .map(s => SECTION_NAME_BY_CODE[s])
    .filter((name): name is string => Boolean(name))

interface IBuildArgs {
  placeName: string
  placeParticle: string
  address: string
  isDetailingOnly: boolean
  hasEngineOil: boolean
  hasTire: boolean
  sortedSections: string[]
  productGroups: IProductGroup[]
  detailingCheapest: IProductForPrice | null
  /** FAQ 1번 전용: car_wash group의 targetProducts 최저가 product (이름 = 상품명) */
  faqDetailingProduct: IProductForPrice | null
  businessHours: ISeoPlace['businessHours']
}

// Q1 - 주요 서비스
const buildServicesQuestion = (args: IBuildArgs): IFaqEntry => {
  const { placeName, isDetailingOnly, hasEngineOil, hasTire, sortedSections } =
    args

  if (hasEngineOil) {
    const others = pickExtraCategoryNames(ENGINE_OIL_SECTION, sortedSections)
    if (others.length === 0) {
      return {
        question: '이 정비소에서 제공하는 주요 정비 서비스는 무엇인가요?',
        answer: `${placeName}의 주요 정비 서비스는 '엔진오일 교환'입니다. 차량에 맞는 엔진오일을 선택하여 예약할 수 있습니다.`,
      }
    }
    return {
      question: '이 정비소에서 제공하는 주요 정비 서비스는 무엇인가요?',
      answer: `${placeName}에서는 엔진오일, ${others.join(', ')} 등 다양한 정비 서비스를 제공합니다. 차량 상태에 맞는 정비 항목을 선택하여 예약하실 수 있습니다.`,
    }
  }

  if (hasTire) {
    const others = pickExtraCategoryNames(TIRE_SECTION, sortedSections)
    if (others.length === 0) {
      return {
        question: '이 정비소에서 제공하는 주요 정비 서비스는 무엇인가요?',
        answer: `${placeName}의 주요 정비 서비스는 '타이어 교체'입니다. 차량에 맞는 타이어를 선택하여 예약하실 수 있습니다.`,
      }
    }
    return {
      question: '이 정비소에서 제공하는 주요 정비 서비스는 무엇인가요?',
      answer: `${placeName}에서는 타이어, ${others.join(', ')} 등 다양한 정비 서비스를 제공합니다. 차량 상태에 맞는 정비 항목을 선택하여 예약하실 수 있습니다.`,
    }
  }

  if (isDetailingOnly && args.faqDetailingProduct) {
    return {
      question:
        '이 자동차 디테일링 전문점에서 제공하는 주요 서비스는 무엇인가요?',
      answer: `${placeName}의 주요 상품은 ${args.faqDetailingProduct.name}입니다. 차량 상태에 맞는 상품을 선택하여 예약하실 수 있습니다.`,
    }
  }

  // fallback
  const fallback = sortedSections
    .slice(0, 3)
    .map(s => SECTION_NAME_BY_CODE[s])
    .filter((name): name is string => Boolean(name))
    .join(', ')
  return {
    question: '이 정비소에서 제공하는 주요 정비 서비스는 무엇인가요?',
    answer: `${placeName}에서는 ${fallback || '다양한 정비 항목'} 등 다양한 정비 서비스를 제공합니다. 차량 상태에 맞는 정비 항목을 선택하여 예약하실 수 있습니다.`,
  }
}

// Q2 - 비용
const buildCostQuestion = (args: IBuildArgs): IFaqEntry => {
  const {
    placeName,
    isDetailingOnly,
    hasEngineOil,
    hasTire,
    productGroups,
    detailingCheapest,
  } = args

  if (hasEngineOil) {
    const engineOilProduct = productGroups.find(
      g => g.section === ENGINE_OIL_SECTION
    )?.lowestPriceTargetProduct
    const originalPrice = parsePrice(engineOilProduct?.price)
    const salePrice = parsePrice(engineOilProduct?.salePrice)
    const couponPrice =
      salePrice > 0 ? applyCouponDiscount(ENGINE_OIL_SECTION, salePrice) : 0
    return {
      question: '엔진오일 교환 비용은 얼마인가요?',
      answer:
        originalPrice > 0
          ? `마이클 앱 기준 ${placeName}의 엔진오일 교환 비용은 ${originalPrice.toLocaleString()}원부터이며, 마이클 앱 쿠폰 적용 시 ${couponPrice.toLocaleString()}원부터입니다. 에어크리너·오일필터·공임비가 포함된 가격이며, 차량 종류와 오일 종류에 따라 비용은 달라질 수 있습니다.`
          : `${placeName}의 엔진오일 교환 비용은 차량 종류와 오일 종류에 따라 달라집니다. 마이클 앱에서 정확한 가격을 확인하실 수 있습니다.`,
    }
  }

  if (hasTire) {
    const price = findSectionPrice(productGroups, TIRE_SECTION)
    return {
      question: '타이어 교체 비용은 얼마인가요?',
      answer:
        price > 0
          ? `마이클 앱 기준 ${placeName}의 타이어 교체 비용은 ${price.toLocaleString()}원부터이며, 공임비가 포함된 가격입니다 (1본 기준). 마이클에서는 더 저렴한 곳이 있을 경우 차액을 보상하는 최저가 보상제를 운영하고 있습니다.`
          : `${placeName}의 타이어 교체 비용은 타이어 종류와 차량에 따라 달라집니다. 마이클 앱에서 정확한 가격을 확인하실 수 있습니다.`,
    }
  }

  if (isDetailingOnly && detailingCheapest) {
    return {
      question: '세차 서비스 비용은 얼마인가요?',
      answer: `마이클 앱 기준 ${placeName}의 세차 서비스는 ${detailingCheapest.price.toLocaleString()}원부터입니다.`,
    }
  }

  return {
    question: '정비 비용은 얼마인가요?',
    answer: `${placeName}의 정비 비용은 항목과 차량에 따라 달라집니다. 마이클 앱에서 정확한 가격을 확인하실 수 있습니다.`,
  }
}

// Q4 - 소요 시간
const buildDurationQuestion = (args: IBuildArgs): IFaqEntry => {
  const { isDetailingOnly, hasEngineOil, hasTire } = args

  if (hasEngineOil) {
    return {
      question: '차량 정비는 보통 얼마나 시간이 걸리나요?',
      answer:
        '엔진오일 교환은 약 20~40분 정도 소요되며, 타이어나 배터리 교체 등 다른 작업은 항목에 따라 시간이 달라질 수 있습니다.',
    }
  }
  if (hasTire) {
    return {
      question: '차량 정비는 보통 얼마나 시간이 걸리나요?',
      answer:
        '타이어 교체는 약 30~60분 정도 소요되며, 배터리나 브레이크패드 교체 등 다른 작업은 항목에 따라 시간이 달라질 수 있습니다.',
    }
  }
  if (isDetailingOnly) {
    return {
      question: '세차는 보통 얼마나 시간이 걸리나요?',
      answer:
        '세차 시간은 선택하신 서비스 종류와 작업 범위에 따라 달라집니다. 정확한 소요 시간은 마이클 앱에서 상품별로 확인하실 수 있습니다.',
    }
  }
  return {
    question: '차량 정비는 보통 얼마나 시간이 걸리나요?',
    answer:
      '정비 작업은 항목에 따라 소요 시간이 달라질 수 있습니다. 자세한 시간은 예약 시 확인하실 수 있습니다.',
  }
}

// businessHours 항목을 "{라벨} {값}" 형식으로 변환. 휴무·비정상 데이터는 "휴무일 {라벨}"로 정규화.
const formatBusinessHoursEntries = (
  businessHours: ISeoPlace['businessHours']
): string | null => {
  if (!businessHours) return null
  const formatted = Object.entries(businessHours)
    .map(([key, value]) => {
      if (!value) return null
      const label = BUSINESS_HOUR_LABELS[key] ?? key
      // value가 단순 요일명만 들어있는 비정상 케이스(예: "일요일": "일요일") 또는 "휴무" 패턴 → 휴무로 정규화
      const isOff = /휴무/.test(value) || value === key
      if (isOff) return `휴무일 ${label}`
      return `${label} ${value}`
    })
    .filter((s): s is string => Boolean(s))
  if (formatted.length === 0) return null
  return formatted.join(', ')
}

// Q6 - 위치/영업시간
const buildBusinessInfoQuestion = (args: IBuildArgs): IFaqEntry => {
  const { placeName, placeParticle, address, businessHours } = args
  const hoursText = formatBusinessHoursEntries(businessHours)

  const prefix = `${placeName}${placeParticle} ${address}에 위치하고 있으며, `
  const suffix = ' 방문 전 예약을 통해 대기 시간을 줄일 수 있습니다.'

  if (!hoursText) {
    return {
      question: '위치와 영업시간은 어떻게 되나요?',
      answer: `${prefix}영업시간은 마이클 앱에서 확인하실 수 있습니다.${suffix}`,
    }
  }

  return {
    question: '위치와 영업시간은 어떻게 되나요?',
    answer: `${prefix}영업 시간은 ${hoursText}입니다.${suffix}`,
  }
}

export const buildFaqItems = (
  place: ISeoPlace,
  productGroups: IProductGroup[]
): IFaqEntry[] => {
  const placeName = place.placeName || '정비소'
  const placeParticle = getSubjectParticle(placeName)
  const sections = productGroups
    .map(g => g.section)
    .filter((s): s is string => Boolean(s))
  const sortedSections = sortSections(sections)
  const hasEngineOil = sortedSections.includes(ENGINE_OIL_SECTION)
  const hasTire = sortedSections.includes(TIRE_SECTION)
  // 디테일링 정비소는 productSections에 car_wash로 들어옴
  const hasCarWash = sortedSections.includes(CAR_WASH_SECTION)
  // 디테일링만 매핑된 경우 (car_wash만 있고 엔진오일/타이어 없음)
  const isDetailingOnly = hasCarWash && !hasEngineOil && !hasTire
  const detailingCheapest = isDetailingOnly
    ? findCheapestProduct(productGroups)
    : null
  const faqDetailingProduct = isDetailingOnly
    ? findCarWashCheapestProduct(productGroups)
    : null

  const address =
    place.coordinates?.streetAddress ||
    place.coordinates?.address ||
    '등록된 주소'

  const buildArgs: IBuildArgs = {
    placeName,
    placeParticle,
    address,
    isDetailingOnly,
    hasEngineOil,
    hasTire,
    sortedSections,
    productGroups,
    detailingCheapest,
    faqDetailingProduct,
    businessHours: place.businessHours,
  }

  const items: IFaqEntry[] = [
    buildServicesQuestion(buildArgs),
    buildCostQuestion(buildArgs),
    {
      question: '예약은 어떻게 할 수 있나요?',
      answer:
        '마이클 앱을 사용하시면 원하는 날짜와 시간을 선택하여 예약할 수 있습니다. 예약 후 방문하시면 대기 없이 바로 시공됩니다. 특히 주말이나 성수기에는 예약 고객 우선으로 진행될 수 있습니다.',
    },
    buildDurationQuestion(buildArgs),
  ]

  // 디테일링만 매핑된 정비소는 Q5(추가 요금) 미노출
  if (!isDetailingOnly) {
    items.push({
      question: '정비 비용 외에 추가 요금이 발생하나요?',
      answer:
        '마이클 앱에서 제공하는 정비 상품은 주요 부품과 공임비가 포함된 가격으로, 예약 시 확인한 금액 외 추가 비용은 발생하지 않습니다.',
    })
  }

  items.push(buildBusinessInfoQuestion(buildArgs))

  return items
}
